/**
 * PRO-2 — Profile editor. THE CORE TRUST AFFORDANCE OF THE PLATFORM.
 *
 * The producer's central fear is being disintermediated — that a buyer will learn who they are
 * and go around MOST. This screen answers that fear by SHOWING them the boundary rather than
 * asking them to trust it: two visually distinct sections with PERSISTENT captions.
 *
 *   «Видно покупателям» (green, eye icon)  — brand_name, country, description,
 *                                            production_capacity, certificates
 *   «Видно только MOST» (neutral, lock)    — legal_name, address, contact_person, phone,
 *                                            export markets
 *
 * The captions are persistent, not tooltips: a producer must be able to see, at a glance and
 * without hovering anything, that their address is not on the buyer's side of the line.
 *
 * The representative contact inside each market gets an EXTRA lock badge. It is the single most
 * bypass-sensitive field in the system — "this factory already exports to Uzbekistan" plus the
 * brand is enough for a buyer to find the local rep and cut MOST out (docs/04 §5). Buyers see
 * NOTHING from `producer_markets` — not the contact, and not even the bare country list.
 *
 * `markets` is a FULL-REPLACE array (openapi PUT /producer/profile): the server deletes every
 * market row and inserts what we send, in one transaction. A duplicate (country, type) pair is a
 * 422, so we validate it client-side too.
 *
 * `rating` is admin-set and read-only here.
 */
import * as React from 'react';
import { Formik, Form, FieldArray, useFormikContext, type FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Eye, Lock, Plus, Star, Trash2, Upload, X } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  ConfirmDialog,
  ErrorState,
  FormErrorPanel,
  FormSkeleton,
  SelectField,
  Separator,
  TextAreaField,
  TextField,
  cn,
  useToast,
} from '@/components/ui';
import { UnsavedGuard } from '@/components/UnsavedGuard';
import { qk } from '@/shared/queryClient';
import * as api from '@/shared/api';
import { ApiError } from '@/shared/http';
import { useConfig, useMe } from '@/shared/session';
import type { MarketType, ProducerProfileUpdate } from '@/shared/contract';

// ── Validation (Yup, mirroring the backend's rules — docs/06 §4.1) ──────────────────────────
function useProfileSchema() {
  const { t } = useTranslation();

  return React.useMemo(
    () =>
      Yup.object({
        brand_name: Yup.string().trim().required(t('validation.required')),
        country: Yup.string()
          .trim()
          .length(2, t('validation.country2'))
          .required(t('validation.required')),
        description: Yup.string().nullable(),
        production_capacity: Yup.string().nullable(),
        legal_name: Yup.string().nullable(),
        address: Yup.string().nullable(),
        contact_person: Yup.string().nullable(),
        phone: Yup.string().nullable(),
        markets: Yup.array()
          .of(
            Yup.object({
              country: Yup.string()
                .trim()
                .length(2, t('validation.country2'))
                .required(t('validation.required')),
              type: Yup.string().oneOf(['can_export', 'already_exports']).required(),
              representative_contact: Yup.string().nullable(),
            }),
          )
          // The server 422s on a duplicate (country, type); catching it here saves a round-trip
          // and points at the offending row instead of failing the whole form.
          .test('unique', t('profile.marketDuplicate'), (markets) => {
            if (!markets) return true;
            const seen = new Set<string>();
            for (const m of markets) {
              const key = `${(m.country ?? '').toLowerCase()}|${m.type}`;
              if (seen.has(key)) return false;
              seen.add(key);
            }
            return true;
          }),
      }),
    [t],
  );
}

export function ProfileScreen() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { toast } = useToast();
  const { data: me } = useMe();
  const schema = useProfileSchema();

  const profile = useQuery({ queryKey: qk.profile, queryFn: api.getProfile });

  const save = useMutation({
    mutationFn: (body: ProducerProfileUpdate) => api.updateProfile(body),
    onSuccess: (updated) => {
      qc.setQueryData(qk.profile, updated);
      toast(t('common.saved'), 'success');
    },
  });

  const submitForApproval = useMutation({
    mutationFn: api.submitProfile,
    onSuccess: () => {
      // Submitting CLEARS `rejection_reason` server-side, so both the profile and the session
      // (whose status may have moved) are stale.
      void qc.invalidateQueries({ queryKey: qk.profile });
      void qc.invalidateQueries({ queryKey: qk.me });
      toast(t('profile.submitted'), 'success');
    },
  });

  if (profile.isLoading) return <FormSkeleton fields={8} />;
  if (profile.isError) {
    return <ErrorState error={profile.error} onRetry={() => void profile.refetch()} />;
  }

  const data = profile.data!;

  const initialValues: ProducerProfileUpdate = {
    brand_name: data.brand_name ?? '',
    country: data.country ?? '',
    description: data.description ?? '',
    production_capacity: data.production_capacity ?? '',
    legal_name: data.legal_name ?? '',
    address: data.address ?? '',
    contact_person: data.contact_person ?? '',
    phone: data.phone ?? '',
    markets: data.markets ?? [],
  };

  const onSubmit = async (
    values: ProducerProfileUpdate,
    helpers: FormikHelpers<ProducerProfileUpdate>,
  ) => {
    try {
      // Empty optional strings go to the server as null, not "" — the column is nullable and a
      // blank string is not the same thing as "not provided".
      await save.mutateAsync({
        ...values,
        description: values.description?.trim() || null,
        production_capacity: values.production_capacity?.trim() || null,
        legal_name: values.legal_name?.trim() || null,
        address: values.address?.trim() || null,
        contact_person: values.contact_person?.trim() || null,
        phone: values.phone?.trim() || null,
        markets: values.markets.map((m) => ({
          ...m,
          representative_contact: m.representative_contact?.trim() || null,
        })),
      });
      helpers.resetForm({ values });
    } catch (err) {
      // Map server field errors back onto the fields by name (docs/06 §4.1).
      if (err instanceof ApiError) {
        const fieldErrors = err.fieldErrors;
        for (const [field, code] of Object.entries(fieldErrors)) {
          const translated = t(`errors.field.${code}`);
          helpers.setFieldError(field, translated.startsWith('errors.') ? code : translated);
        }
      }
    }
  };

  // The producer may re-submit whenever they are still in the approval gate. The endpoint clears
  // any previous rejection_reason, which is what puts them back in the admin's queue.
  const canSubmit = me?.status === 'pending_approval';

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">{t('profile.title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('profile.subtitle')}</p>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={onSubmit}
        enableReinitialize
        validateOnBlur
      >
        {(formik) => (
          <Form className="space-y-6">
            <UnsavedGuard when={formik.dirty && !formik.isSubmitting} />

            {/* ══════════════════ VISIBLE TO BUYERS ══════════════════ */}
            <VisibilitySection
              tone="public"
              icon={Eye}
              title={t('profile.publicSection')}
              hint={t('profile.publicHint')}
            >
              <div className="grid gap-5 md:grid-cols-2">
                <TextField name="brand_name" label={t('profile.brandName')} required />
                <TextField
                  name="country"
                  label={t('profile.country')}
                  required
                  maxLength={2}
                  placeholder="TM"
                  className="md:max-w-[10rem]"
                />
              </div>

              <TextAreaField name="description" label={t('profile.description')} rows={3} />
              <TextField
                name="production_capacity"
                label={t('profile.productionCapacity')}
              />

              {/* Admin-set, read-only. Rendered as text, not as a disabled input — a disabled
                  input invites the producer to try to edit it. */}
              <div className="space-y-1.5">
                <span className="block text-sm font-medium text-foreground/80">
                  {t('profile.rating')}
                </span>
                <div className="flex items-center gap-2">
                  {data.rating ? (
                    <Badge tone="green">
                      <Star className="h-3 w-3 fill-current" aria-hidden />
                      {data.rating}
                    </Badge>
                  ) : (
                    <span className="text-sm text-muted-foreground/70">{t('profile.ratingNone')}</span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{t('profile.ratingHint')}</p>
              </div>

              <Separator className="my-2" />

              <CertificatesEditor />
            </VisibilitySection>

            {/* ══════════════════ VISIBLE ONLY TO MOST ══════════════════ */}
            <VisibilitySection
              tone="private"
              icon={Lock}
              title={t('profile.privateSection')}
              hint={t('profile.privateHint')}
            >
              <div className="grid gap-5 md:grid-cols-2">
                <TextField name="legal_name" label={t('profile.legalName')} />
                <TextField name="contact_person" label={t('profile.contactPerson')} />
                <TextField name="address" label={t('profile.address')} />
                <TextField name="phone" label={t('profile.phone')} type="tel" />
              </div>

              <Separator className="my-2" />

              <MarketsEditor />
            </VisibilitySection>

            {save.isError ? <FormErrorPanel error={save.error} /> : null}

            <div className="flex flex-wrap items-center gap-3">
              <Button type="submit" loading={formik.isSubmitting || save.isPending}>
                {t('common.save')}
              </Button>

              {canSubmit ? (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    loading={submitForApproval.isPending}
                    // Submitting a dirty form would send the OLD profile for review while the
                    // producer stares at their unsaved edits. Save first.
                    disabled={formik.dirty}
                    onClick={() => submitForApproval.mutate()}
                  >
                    {t('profile.submitForApproval')}
                  </Button>
                  <p className="text-xs text-muted-foreground">{t('profile.submitHint')}</p>
                </>
              ) : null}
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}

// ── The visibility section wrapper — the trust affordance itself ────────────────────────────
function VisibilitySection({
  tone,
  icon: Icon,
  title,
  hint,
  children,
}: {
  tone: 'public' | 'private';
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  hint: string;
  children: React.ReactNode;
}) {
  const isPublic = tone === 'public';

  return (
    <Card className={cn('overflow-hidden', isPublic ? 'border-emerald-200' : 'border-input')}>
      {/* The caption is PERSISTENT and always visible — never a tooltip. The producer must be
          able to see the boundary without interacting with anything. */}
      <div
        className={cn(
          'flex items-start gap-3 border-b px-5 py-4',
          isPublic ? 'border-emerald-200 bg-emerald-50' : 'border-border bg-muted',
        )}
      >
        <Icon
          className={cn('mt-0.5 h-5 w-5 shrink-0', isPublic ? 'text-emerald-700' : 'text-muted-foreground')}
          aria-hidden
        />
        <div>
          <h2
            className={cn(
              'text-sm font-semibold',
              isPublic ? 'text-emerald-900' : 'text-foreground',
            )}
          >
            {title}
          </h2>
          <p className={cn('mt-0.5 text-xs', isPublic ? 'text-emerald-800' : 'text-muted-foreground')}>
            {hint}
          </p>
        </div>
      </div>

      <CardContent className="space-y-5 p-5">{children}</CardContent>
    </Card>
  );
}

// ── Export markets (MOST-only) ──────────────────────────────────────────────────────────────
function MarketsEditor() {
  const { t } = useTranslation();
  const { values, errors } = useFormikContext<ProducerProfileUpdate>();

  const typeOptions: Array<{ value: MarketType; label: string }> = [
    { value: 'can_export', label: t('profile.marketCanExport') },
    { value: 'already_exports', label: t('profile.marketAlreadyExports') },
  ];

  // The array-level "duplicate pair" error from the Yup `.test()`.
  const arrayError = typeof errors.markets === 'string' ? errors.markets : undefined;

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-medium text-foreground/80">{t('profile.markets')}</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">{t('profile.marketsHint')}</p>
      </div>

      {arrayError ? (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" role="alert">
          {arrayError}
        </p>
      ) : null}

      <FieldArray name="markets">
        {({ push, remove }) => (
          <div className="space-y-3">
            {values.markets.map((market, i) => (
              <div
                key={i}
                className="rounded-md border border-border bg-muted/50 p-4"
              >
                <div className="grid gap-4 md:grid-cols-[8rem_1fr_auto]">
                  <TextField
                    name={`markets.${i}.country`}
                    label={t('profile.marketCountry')}
                    maxLength={2}
                    placeholder="UZ"
                  />
                  <SelectField
                    name={`markets.${i}.type`}
                    label={t('profile.marketType')}
                    options={typeOptions}
                  />
                  <div className="flex items-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => remove(i)}
                      aria-label={t('profile.marketRemove')}
                      title={t('profile.marketRemove')}
                    >
                      <Trash2 className="h-4 w-4 text-muted-foreground" aria-hidden />
                    </Button>
                  </div>
                </div>

                {/* Only meaningful for `already_exports` — and that is exactly the case where it
                    is dangerous. The extra lock badge is not decoration: this one field is the
                    difference between MOST brokering the deal and being cut out of it. */}
                {market.type === 'already_exports' ? (
                  <div className="mt-4 rounded-md border border-input bg-card p-3">
                    <div className="mb-2 flex items-center gap-2">
                      <Badge tone="neutral">
                        <Lock className="h-3 w-3" aria-hidden />
                        {t('profile.lockedTitle')}
                      </Badge>
                    </div>
                    <TextField
                      name={`markets.${i}.representative_contact`}
                      label={t('profile.marketRep')}
                      hint={t('profile.marketRepHint')}
                    />
                  </div>
                ) : null}
              </div>
            ))}

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => push({ country: '', type: 'can_export', representative_contact: '' })}
            >
              <Plus className="h-3.5 w-3.5" aria-hidden />
              {t('profile.marketAdd')}
            </Button>
          </div>
        )}
      </FieldArray>
    </div>
  );
}

// ── Certificates (buyer-visible) ────────────────────────────────────────────────────────────
function CertificatesEditor() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { toast } = useToast();
  const { data: config } = useConfig();
  const profile = useQuery({ queryKey: qk.profile, queryFn: api.getProfile });

  const [title, setTitle] = React.useState('');
  const [pending, setPending] = React.useState<File | null>(null);
  const [progress, setProgress] = React.useState<number | null>(null);
  const [deleting, setDeleting] = React.useState<{ id: string; title: string } | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const certs = profile.data?.certificates ?? [];
  const maxCerts = config?.max_certificates_per_producer ?? 20;
  const maxBytes = config?.max_certificate_bytes ?? 10 * 1024 * 1024;
  const ACCEPT = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];

  const upload = useMutation({
    mutationFn: async () => {
      if (!pending) throw new Error('no file');
      return api.uploadCertificate(title.trim() || pending.name, pending, setProgress);
    },
    onSuccess: () => {
      setPending(null);
      setTitle('');
      setProgress(null);
      void qc.invalidateQueries({ queryKey: qk.profile });
      toast(t('common.saved'), 'success');
    },
    onError: () => setProgress(null),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteCertificate(id),
    onSuccess: () => {
      setDeleting(null);
      void qc.invalidateQueries({ queryKey: qk.profile });
    },
  });

  // Client-side pre-checks MIRROR the server limits from GET /config (docs/06 §10) — they are a
  // courtesy, not a security control; the server re-validates.
  const pick = (file: File | undefined) => {
    if (!file) return;
    if (!ACCEPT.includes(file.type)) {
      toast(t('wizard.badType'), 'error');
      return;
    }
    if (file.size > maxBytes) {
      toast(t('wizard.tooLarge', { size: `${Math.round(maxBytes / 1024 / 1024)} MB` }), 'error');
      return;
    }
    setPending(file);
  };

  return (
    <div className="space-y-3">
      <div>
        <h3 className="text-sm font-medium text-foreground/80">{t('profile.certificates')}</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {t('profile.certificatesHint', { max: maxCerts })}
        </p>
      </div>

      {certs.length > 0 ? (
        <ul className="divide-y divide-hairline rounded-md border border-border">
          {certs.map((c) => (
            <li key={c.id} className="flex items-center gap-3 px-3 py-2">
              <a
                href={c.url}
                target="_blank"
                rel="noreferrer"
                className="flex-1 truncate text-sm text-link hover:underline"
              >
                {c.title}
              </a>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setDeleting({ id: c.id, title: c.title })}
                aria-label={t('profile.certificateDelete')}
              >
                <X className="h-4 w-4 text-muted-foreground/70" aria-hidden />
              </Button>
            </li>
          ))}
        </ul>
      ) : null}

      {certs.length < maxCerts ? (
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[14rem] flex-1 space-y-1.5">
            <label htmlFor="cert-title" className="block text-sm font-medium text-foreground/80">
              {t('profile.certificateTitle')}
            </label>
            <input
              id="cert-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="flex h-9 w-full rounded-md border border-input bg-card px-3 text-sm transition-colors focus-visible:outline-none focus-visible:border-ring focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0"
            />
          </div>

          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT.join(',')}
            className="sr-only"
            onChange={(e) => pick(e.target.files?.[0])}
          />

          <Button type="button" variant="outline" onClick={() => inputRef.current?.click()}>
            <Upload className="h-4 w-4" aria-hidden />
            {pending ? pending.name : t('profile.certificateAdd')}
          </Button>

          {pending ? (
            <Button
              type="button"
              onClick={() => upload.mutate()}
              loading={upload.isPending}
            >
              {progress !== null ? `${progress}%` : t('common.save')}
            </Button>
          ) : null}
        </div>
      ) : null}

      <ConfirmDialog
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate(deleting.id)}
        loading={remove.isPending}
        title={t('profile.certificateDeleteConfirm', { title: deleting?.title ?? '' })}
        confirmLabel={t('common.delete')}
        destructive
      />
    </div>
  );
}
